import { AxiosResponse } from 'axios'
import { QueryClient, QueryKey, queryOptions } from '@tanstack/react-query'
import { NUsers } from '../interfaces/services/users.interfaces'
import {
  UserUpdatedAction,
  UserUpdatedPayload,
} from '../generated/api/live-events'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { postFeedQueryKey, postPinnedQueryKey } from './posts'

export const getUser = (
  id: string,
  societyId?: string
): Promise<AxiosResponse<NUsers.NGetById.IResponse>> => {
  return axiosAuth.get<NUsers.NGetById.IResponse>(
    createUrl(`/users/user/${id}`, { societyId })
  )
}

export const createUser = (
  body: NUsers.NCreate.IRequestBody
): Promise<AxiosResponse<NUsers.NCreate.IResponse>> => {
  // No sendLink either: the mail is not optional any more. It carries the only
  // way into the account, so a signup that skipped it would create something
  // nobody can sign in to.
  return axiosAuth.post<NUsers.NCreate.IResponse>(
    createUrl(`/users/user`),
    body
  )
}

export const patchUser = (variables: {
  id: string
  body: NUsers.NPatch.IRequestBody
  parameters?: {
    [key: string]: string
  }
}): Promise<AxiosResponse<NUsers.NPatch.IResponse>> => {
  return axiosAuth.patch<NUsers.NPatch.IResponse>(
    createUrl(`/users/user/${variables.id}`, {
      ...variables.parameters,
    }),
    variables.body
  )
}

export const deleteUser = (
  id: string,
  body: NUsers.NDelete.IRequestBody
): Promise<AxiosResponse<NUsers.NDelete.IResponse>> => {
  return axiosAuth.delete<NUsers.NDelete.IResponse>(
    createUrl(`/users/user/${id}`),
    {
      data: body,
    }
  )
}

// Does not change the address. It mails a confirmation link to the address
// given here, and the account only moves once that link is opened -- so a typo
// costs nothing, where before it stranded the account on an inbox nobody reads.
export const updateEmail = (
  email: string,
  password: string
): Promise<AxiosResponse<NUsers.NUpdateEmail.IResponse>> => {
  const body: NUsers.NUpdateEmail.IRequestBody = {
    newEmail: email,
    password,
  }
  return axiosAuth.post<NUsers.NUpdateEmail.IResponse>(
    createUrl(`/users/update-email`),
    body
  )
}

// Whether a mailed password link can still be used, without using it up. The
// pages a link opens ask this before showing the password fields.
export const checkResetCode = (
  code: string
): Promise<AxiosResponse<NUsers.NCheckResetCode.IResponse>> => {
  const body: NUsers.NCheckResetCode.IRequestBody = { code }
  return axiosAuth.post<NUsers.NCheckResetCode.IResponse>(
    createUrl(`/users/check-reset-code`),
    body
  )
}

// The other end of that link. No session comes back: the code proves the inbox,
// not the account, and whoever asked for the change proved the account with
// their password already.
export const confirmEmailChange = (
  code: string
): Promise<AxiosResponse<NUsers.NConfirmEmailChange.IResponse>> => {
  const body: NUsers.NConfirmEmailChange.IRequestBody = { code }
  return axiosAuth.post<NUsers.NConfirmEmailChange.IResponse>(
    createUrl(`/users/confirm-email-change`),
    body
  )
}

export const updatePassword = (
  newPassword: string,
  oldPassword: string
): Promise<AxiosResponse<NUsers.NUpdatePassword.IResponse>> => {
  const body: NUsers.NUpdatePassword.IRequestBody = {
    newPassword,
    oldPassword,
  }
  return axiosAuth.post<NUsers.NUpdatePassword.IResponse>(
    createUrl(`/users/update-password`),
    body
  )
}

// isEmailRegistered used to live here. The endpoint it called is gone -- it
// answered true/false for any address without authentication, so anyone could
// use it to find out who has a Boappa account.

export const sendResetPassword = (
  email: string
): Promise<AxiosResponse<NUsers.NSendResetPassword.IResponse>> => {
  const body: NUsers.NSendResetPassword.IRequestBody = {
    email,
    sendLink: true,
  }
  return axiosAuth.post<NUsers.NSendResetPassword.IResponse>(
    createUrl(`/users/send-reset-password`),
    body
  )
}

export const resetPassword = (
  code: string,
  password: string
): Promise<AxiosResponse<NUsers.NResetPassword.IResponse>> => {
  const body: NUsers.NResetPassword.IRequestBody = {
    code,
    password,
  }
  return axiosAuth.post<NUsers.NResetPassword.IResponse>(
    createUrl(`/users/reset-password`),
    body
  )
}

// resendVerifyCode used to live here. Its endpoint is gone -- sendResetPassword
// mails the same link for an address that has never been verified, and words it
// as the last step of signing up rather than as a reset.

export const createUserDevice = (
  userId: string,
  body: NUsers.NAddUserDevice.IRequestBody
): Promise<AxiosResponse<NUsers.NAddUserDevice.IResponse>> => {
  return axiosAuth.post<NUsers.NAddUserDevice.IResponse>(
    createUrl(`/users/user/${userId}/devices`),
    body
  )
}

// verifyUser used to live here too. resetPassword took over its job: setting a
// password proves the address, so there is nothing left for a separate call to
// verify.

export const userQueries = {
  getUser: (userId?: string, societyId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'user', entity: 'getUser', userId, societyId }],
      queryFn: async ({ queryKey }) => {
        return (
          await getUser(queryKey[0].userId as string, queryKey[0].societyId)
        ).data.data
      },
      enabled: !!userId,
    }),
}

export const applyUserUpdated = async (
  client: QueryClient,
  { action }: UserUpdatedPayload,
  userId: string | undefined,
  signOut: () => void
): Promise<void> => {
  if (action === 'deleted') {
    signOut()
    return
  }
  if (action === 'profile' || action === 'settings') {
    await client.invalidateQueries()
    return
  }
  const ownUser: QueryKey[] = [
    [{ scope: 'auth', entity: 'user', userId }],
    [{ scope: 'user', entity: 'getUser', userId }],
  ]
  const queryKeys: Partial<Record<UserUpdatedAction, QueryKey[]>> = {
    'feed-filters': [...ownUser, postFeedQueryKey, postPinnedQueryKey],
    'notification-preferences': ownUser,
  }
  await Promise.all(
    (queryKeys[action] ?? []).map((queryKey) =>
      client.invalidateQueries({ queryKey })
    )
  )
}
